import { client } from '~/api/client';
import {
  CreateHtmlDocumentTemplateDocument,
  CreateHtmlDocumentTemplateMutation,
  CreateHtmlDocumentTemplateMutationVariables,
  TemplateDesignSource,
  type VisibilityConfigInput
} from '~/api/graphql';

export type SaveTemplateResult = {
  templateId: string;
  title: string;
};

/**
 * Saves an HTML document as a template (as-is, without conversion).
 * The backend will automatically extract body content from the HTML.
 *
 * `designId` makes the template remember the design its source document renders with, and null
 * makes it a None template, for a document that renders with no design. Absent, it is left off
 * the request and the template starts documents on the agency default. Start and end pages are
 * not sent: the server picks them from the template's own files.
 *
 * Returns the template id and title so callers can build the link and message.
 */
export async function saveTemplate(
  htmlContent: string,
  title: string,
  description: string,
  visibility?: VisibilityConfigInput,
  designId?: string | null
): Promise<SaveTemplateResult> {
  const result = await client.mutate<
    CreateHtmlDocumentTemplateMutation,
    CreateHtmlDocumentTemplateMutationVariables
  >({
    mutation: CreateHtmlDocumentTemplateDocument,
    variables: {
      input: {
        title,
        description,
        bodyTemplate: htmlContent,
        visibility,
        ...(designId === null
          ? { designSource: TemplateDesignSource.None }
          : designId && { designId })
      }
    }
  });

  if (result.errors?.length) {
    throw new Error(result.errors.map((e) => e.message).join(', '));
  }

  if (!result.data?.createHtmlDocumentTemplate) {
    throw new Error('Template creation failed: No data returned');
  }

  const templateId = result.data.createHtmlDocumentTemplate.id;
  return { templateId, title };
}
